import { DependencyList, useEffect, useRef } from 'react';
import { debounce } from 'lodash';

/**
 * Used to debounce a function in a function component.
 */
const useDebouncedCallback = <
    // typing supports 0-5 arguments
    Args extends
        | []
        | [unknown]
        | [unknown, unknown]
        | [unknown, unknown, unknown]
        | [unknown, unknown, unknown, unknown]
        | [unknown, unknown, unknown, unknown, unknown],
    ReturnType,
>(
    fn: (...args: Args) => ReturnType,
    delay: number,
    deps: DependencyList,
) => {
    const fnRef = useRef(fn);

    // In here, useRef will make sure that the debounced function does not get recreated every single time
    const debouncedFn = useRef(
        debounce<(...args: Args) => ReturnType>((...args: Args) => fnRef.current(...args), delay),
    );

    // On every dependency change, recreate the function ref so that it will have the latest values
    useEffect(() => {
        fnRef.current = fn;

        return () => {
            debouncedFn.current.cancel();
        };
    }, deps);

    return debouncedFn.current;
};

export default useDebouncedCallback;
